<!--
  TokenFieldMenu.vue -- the contextual "Insert a field" menu (PR-4).

  Shared by the subject and body editors. The token list is passed in from
  the active email type's catalog entry -- NEVER a hardcoded global list --
  so refund shows {refund_amount}, shipping shows {tracking_number}, etc.

  a11y (ADR 0028): the menu is a listbox of buttons; each option is
  keyboard-focusable and labeled. Escape / outside-click closes.
-->
<template>
  <div class="token-field-menu" data-testid="token-field-menu">
    <!-- Outside-click scrim -->
    <div class="token-field-menu__scrim" @mousedown="$emit('close')" />
    <div
      class="token-field-menu__panel"
      role="listbox"
      aria-label="Insert a field"
    >
      <div class="token-field-menu__header">
        <div class="token-field-menu__title">Insert a field</div>
        <div class="token-field-menu__sub">
          Fields available for <b>{{ scopeLabel }}</b
          >. They fill in automatically per recipient.
        </div>
      </div>
      <div class="token-field-menu__list">
        <button
          v-for="tok in tokens"
          :key="tok.key"
          type="button"
          class="token-field-menu__item"
          role="option"
          :data-testid="`token-field-option-${tok.key}`"
          :aria-label="`Insert field ${tok.label}, for example ${tok.sample}`"
          @mousedown.prevent="$emit('pick', tok.key)"
        >
          <span class="token-field-menu__chip">{{ tok.label }}</span>
          <span class="token-field-menu__meta">
            <span class="token-field-menu__name">{{ tok.label }}</span>
            <span class="token-field-menu__ex"
              >e.g. <b>{{ tok.sample }}</b></span
            >
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TokenFieldMenu",
  props: {
    // [{ key, label, sample }] -- the active type's contextual tokens.
    tokens: { type: Array, required: true },
    scopeLabel: { type: String, default: "" },
  },
  emits: ["pick", "close"],
};
</script>

<style scoped>
.token-field-menu__scrim {
  position: fixed;
  inset: 0;
  z-index: 30;
}
.token-field-menu__panel {
  position: absolute;
  top: 40px;
  right: 0;
  z-index: 40;
  width: 320px;
  background: #fff;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 12px;
  box-shadow: 0 11px 15px -7px rgba(0, 0, 0, 0.2),
    0 24px 38px 3px rgba(0, 0, 0, 0.14);
  overflow: hidden;
}
.token-field-menu__header {
  padding: 10px 14px 8px;
  border-bottom: 1px solid var(--tf-border, #cfd8e3);
}
.token-field-menu__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-fg-1, #133752);
}
.token-field-menu__sub {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
  margin-top: 3px;
  line-height: 1.4;
}
.token-field-menu__list {
  max-height: 260px;
  overflow-y: auto;
}
.token-field-menu__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid #f0f2f5;
  background: #fff;
  cursor: pointer;
}
.token-field-menu__item:hover,
.token-field-menu__item:focus-visible {
  background: #f3f8fd;
  outline: none;
}
.token-field-menu__chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: var(--tf-token-fg, #2456b8);
  background: var(--tf-token-bg, #eef4ff);
  border: 1px solid var(--tf-token-border, #cfe0fb);
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
}
.token-field-menu__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.token-field-menu__name {
  font-size: 13px;
  color: var(--tf-fg-1, #133752);
}
.token-field-menu__ex {
  font-size: 11.5px;
  color: var(--tf-fg-4, #78909c);
}
</style>
